import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CalendarClock, FileText } from "lucide-react";

import { formatDisplayDate } from "@/lib/dateFormat";
import { listInvoices } from "@/services/invoiceService";

function formatAmount(amount: number, currency: string): string {
  const formatted = amount.toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${currency} ${formatted}`;
}

/**
 * Draft invoices summary for the dashboard — mirrors DashboardRecurringInvoicesCard's
 * simple-row style. Scheduled drafts (scheduledSendAt set) lead, soonest first;
 * plain drafts follow, most recently touched first. Without this, a draft created
 * or scheduled elsewhere (e.g. mobile) had no dashboard surface at all — only
 * showed up once the user opened the full invoices list.
 */
export function DashboardDraftInvoicesCard(): JSX.Element | null {
  const { t, i18n } = useTranslation();

  const { data } = useQuery({
    queryKey: ["invoices", "dashboard-drafts"],
    queryFn: () => listInvoices({ status: "draft", limit: 50 }),
    staleTime: 60_000,
    retry: false,
  });

  const rows = useMemo(() => {
    const drafts = data?.data ?? [];
    const scheduled = drafts
      .filter((d) => d.scheduledSendAt)
      .sort((a, b) => (a.scheduledSendAt ?? "").localeCompare(b.scheduledSendAt ?? ""));
    const unscheduled = drafts
      .filter((d) => !d.scheduledSendAt)
      .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));
    return [...scheduled, ...unscheduled].slice(0, 3);
  }, [data]);

  if (rows.length === 0) return null;

  return (
    <section>
      <div className="fx-section-head">
        <span className="fx-eyebrow">{t("dashboard.draftsCard.title", "Borradores")}</span>
        <Link to="/invoices?status=draft" className="fx-section-head-link">
          {t("dashboard.activity.viewAll", "Ver todas →")}
        </Link>
      </div>

      <div className="space-y-2">
        {rows.map((inv) => {
          const name =
            inv.receiverSnapshot?.name?.trim() ||
            t("dashboard.recurringCard.fallbackClient", "Cliente");
          const amount = formatAmount(
            inv.totals?.total?.amount ?? 0,
            inv.totals?.total?.currency ?? inv.currency
          );
          return (
            <Link key={inv.id} to={`/invoices/${inv.id}`} className="fx-recurring-simple">
              <span className="fx-recurring-simple-ico">
                {inv.scheduledSendAt ? (
                  <CalendarClock aria-hidden />
                ) : (
                  <FileText aria-hidden />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="fx-recurring-simple-title block truncate">{name}</span>
                <span className="fx-recurring-simple-sub block">
                  {inv.scheduledSendAt
                    ? t("dashboard.draftsCard.scheduledFor", "Programada para {{dateTime}}", {
                        dateTime: formatDisplayDate(inv.scheduledSendAt, {
                          locale: i18n.language,
                          includeTime: true,
                        }),
                      })
                    : t("dashboard.draftsCard.plainDraft", "Borrador")}
                  {" · "}
                  {amount}
                </span>
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
